/**
 * Tiny event-bus-driven state store.
 */

// Deprecated. Was a client-side lock window before bettingEndsAt; in practice
// it caused silent bet rejections (toast "Betting closed" with no API call,
// no Railway log) when the clock-offset estimate was wrong. The server now
// owns the entire grace window (750ms past bettingEndsAt). Kept exported as
// 0 for back-compat with any caller still importing it; never gate on it.
export const LOCK_MS = 0;

const state = {
  config: null,
  round: null,            // current round (server view)
  remainingMs: 0,
  bettingEndsAt: 0,       // absolute server deadline (ms epoch); 0 = none
  spinEndsAt: 0,          // absolute server deadline (ms epoch); 0 = none
  betsBySlot: {},         // aggregate from server
  myBets: {},             // slotId -> amount (this round)
  selectedAmount: 0,
  balance: null,          // diamonds (best-effort, refreshed from server-driven balance returned by /bet)
  status: 'idle',         // idle | betting | spinning | result
  winners: [],
  lastFinishedRound: null,
  audioMuted: false,
  // Session-only running totals (since this game session opened)
  sessionProfit: 0,        // net win/loss in diamonds since open
  resultHistory: [],       // [{slotId, emoji, isNew}] last 8 finished rounds (newest first)
  activity: [],            // 24h per-user activity totals from ferris_activity
  activityUpdatedAt: 0,
  // Server clock reconciliation: server-time = clientNow + clockOffset.
  // Updated on every payload that carries serverNow (round events,
  // /round/current, ferris_time_sync heartbeat, ferris_round_snapshot).
  // The clockOffset is now a *fallback* only: visible countdown is driven by
  // serverRemainingMs minus a monotonic delta since lastSyncReceivedAt, which
  // is immune to clock-offset bugs. See applyServerNow() for details.
  clockOffset: 0,
  _clockOffsetSampled: false,
  _clockSampleCount: 0,
  _clockBestRtt: Infinity,
  _clockLastRttMs: null,
  // Server-authoritative countdown source. Updated on every payload that
  // carries remainingMs (round_started, time_sync, round_snapshot, etc.).
  // The HUD reads `serverRemainingMs - (Date.now() - lastSyncReceivedAt)`.
  serverRemainingMs: 0,
  lastSyncReceivedAt: 0,
  lastSyncSource: null, // 'http' | 'ws_time_sync' | 'ws_round_event' | 'ws_snapshot'
};

const listeners = new Set();

export function getState() { return state; }

export function setState(patch) {
  Object.assign(state, patch);
  listeners.forEach((fn) => fn(state));
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function clearMyBets() { state.myBets = {}; }

/**
 * Reconcile the client clock with the server. Called whenever a payload
 * carries `serverNow`.
 *
 * `rttMs` is the *measured* round-trip in ms when known:
 *  - HTTP fetches: real RTT measured around `fetch()`.
 *  - WS pongs: real RTT measured around our ping (`Date.now() - clientSentAt`).
 *  - Other WS events: pass `null`. We still update offset but with a low weight.
 *
 * IMPORTANT: do NOT compute RTT from `Date.now() - payload._emittedAt` for the
 * first samples — that subtraction is contaminated by clock skew (it equals
 * RTT/2 + skew, not RTT), and using it as RTT poisons the offset. It's only
 * usable as a lower-bound *staleness* check, not as RTT.
 *
 * Algorithm (no lock-in):
 *   sample = serverNow + rtt/2 - clientRecvAt    // half-RTT bias correction
 *   weight = clamp(1 - rtt/500, 0.1, 0.6)         // higher confidence for low RTT
 *   first sample → set directly; subsequent → EMA blend.
 */
export function applyServerNow(serverNow, rttMs) {
  if (!serverNow || typeof serverNow !== 'number') return;
  const clientRecvAt = Date.now();
  const halfRtt = typeof rttMs === 'number' && isFinite(rttMs) && rttMs >= 0 ? rttMs / 2 : 0;
  const sample = (serverNow + halfRtt) - clientRecvAt;
  state._clockSampleCount = (state._clockSampleCount || 0) + 1;
  if (typeof rttMs === 'number' && isFinite(rttMs)) {
    state._clockLastRttMs = rttMs;
    if (rttMs < state._clockBestRtt) state._clockBestRtt = rttMs;
  }

  if (!state._clockOffsetSampled) {
    state.clockOffset = sample;
    state._clockOffsetSampled = true;
  } else {
    const rttForWeight = typeof rttMs === 'number' && isFinite(rttMs) ? rttMs : 350;
    const weight = Math.max(0.1, Math.min(0.6, 1 - rttForWeight / 500));
    state.clockOffset = state.clockOffset * (1 - weight) + sample * weight;
  }
}

/**
 * Apply a server payload that carries authoritative `remainingMs`. The HUD
 * countdown reads this minus the monotonic delta since `lastSyncReceivedAt`,
 * making it immune to clock-offset drift.
 */
export function applyServerRemaining(remainingMs, source) {
  if (typeof remainingMs !== 'number' || !isFinite(remainingMs)) return;
  state.serverRemainingMs = Math.max(0, remainingMs);
  state.lastSyncReceivedAt = Date.now();
  state.lastSyncSource = source || 'unknown';
}

/**
 * Authoritative remaining ms in the current phase, derived from the most
 * recent server payload minus monotonic time elapsed since it arrived.
 * Returns 0 if no payload has been received yet.
 */
export function authoritativeRemainingMs() {
  if (!state.lastSyncReceivedAt) return 0;
  const elapsed = Date.now() - state.lastSyncReceivedAt;
  return Math.max(0, state.serverRemainingMs - elapsed);
}

/** Server-aligned current time in ms epoch. */
export function serverNow() {
  return Date.now() + state.clockOffset;
}
